import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { KeyRound } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { supabase } from '@/integrations/supabase/client';
import { signInWithPasskey } from '@/hooks/usePasskeys';
import { Link } from 'react-router-dom';

const loginSchema = z.object({
  email: z.string().email({ message: 'Wprowadź poprawny adres email' }),
  password: z.string().min(6, { message: 'Hasło musi mieć co najmniej 6 znaków' }),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const LoginForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const navigate = useNavigate();

  const handlePasskeyLogin = async () => {
    setPasskeyLoading(true);
    try {
      const ok = await signInWithPasskey();
      if (ok) {
        toast.success("Zalogowano passkey'em");
        navigate('/dashboard', { replace: true });
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    try {
      console.log('Attempting login with email:', data.email);

      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) {
        console.error('Login error response:', error);
        throw error;
      }

      if (!authData.session) {
        console.error('No session returned after login');
        toast.error('Błąd sesji podczas logowania');
        return;
      }

      console.log('Login successful, session obtained:', authData.session.expires_at);
      toast.success('Zalogowano pomyślnie');

      // Navigate optimistically — organization check happens on the destination page.
      // Fire-and-forget the org lookup to decide between /dashboard and /setup.
      navigate('/dashboard', { replace: true });

      (async () => {
        const { data: orgData, error: orgError } = await supabase.rpc(
          'get_user_organization_details',
          { user_id: authData.user.id }
        );
        if (orgError || !orgData || !(orgData as any).id) {
          navigate('/setup', { replace: true });
        }
      })();
    } catch (error: any) {
      console.error('Login error:', error);
      let errorMessage = 'Błąd logowania. Sprawdź dane i spróbuj ponownie.';

      if (error.message) {
        if (error.message.includes('Invalid login')) {
          errorMessage = 'Nieprawidłowe dane logowania. Sprawdź email i hasło.';
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Email nie został potwierdzony. Sprawdź swoją skrzynkę pocztową.';
        } else if (error.message.includes('Load failed') || error.message.includes('fetch')) {
          errorMessage =
            'Problem z połączeniem. Spróbuj odświeżyć stronę lub zmienić przeglądarkę.';
        }
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="twoj@email.pl" {...field} autoComplete="email" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Hasło</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder="Hasło"
                  {...field}
                  autoComplete="current-password"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-sm text-primary hover:underline">
            Zapomniałeś hasła?
          </Link>
        </div>
        <Button type="submit" className="w-full" disabled={isLoading || passkeyLoading}>
          {isLoading ? 'Logowanie...' : 'Zaloguj się'}
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">lub</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handlePasskeyLogin}
          disabled={isLoading || passkeyLoading}
        >
          <KeyRound className="mr-2 h-4 w-4" />
          {passkeyLoading ? 'Weryfikacja...' : "Zaloguj passkey'em"}
        </Button>
      </form>
    </Form>
  );
};

export default LoginForm;
